import React, { useRef, useState } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { InView } from 'react-intersection-observer'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { GuildAccessNotch } from '@/molecules/GuildAccessNotch'
import {
  ContentInteraction,
  IMPRESSION_VISIBILITY_THRESHOLD,
  useCardInteractions,
} from '@/services/Analytics/ContentInteractions'
import { LinkViewModel } from '@/services/RenderService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'

interface LargePortraitLinkProps {
  link: LinkViewModel
  className?: string
  isGuildMember?: boolean
  loading?: boolean
  isGrid?: boolean
  itemIndex?: number
  createInteraction?: (link: LinkViewModel, itemIndex: number) => ContentInteraction | null
  trackInteraction?: (interaction: ContentInteraction, allowAutoImpression?: boolean) => void
  hoverStartTimeRef?: React.MutableRefObject<Map<string, number>>
  impressionTimeoutRef?: React.MutableRefObject<Map<string, NodeJS.Timeout>>
  hasTrackedImpression?: React.MutableRefObject<Set<string>>
}

export function LargePortraitLink(props: LargePortraitLinkProps) {
  const track = useSafeTrack()
  const [isLoadingImage, setIsLoadingImage] = useState(true)
  const cardRef = useRef<HTMLDivElement>(null)
  const {
    className,
    isGuildMember = false,
    link,
    itemIndex = 0,
    createInteraction,
    hoverStartTimeRef,
    impressionTimeoutRef,
    hasTrackedImpression,
  } = props

  const contentId = link.id ?? ''

  const onClickTrack = React.useCallback(() => {
    if (link.track) track(link.track?.eventName, link.track?.payload)
  }, [link.track, track])

  // Only use card interactions if the required props are provided
  const shouldUseInteractions = Boolean(
    createInteraction && hoverStartTimeRef && impressionTimeoutRef && hasTrackedImpression,
  )

  const defaultHoverStartTimeRef = useRef(new Map<string, number>())
  const defaultImpressionTimeoutRef = useRef(new Map<string, NodeJS.Timeout>())
  const defaultHasTrackedImpression = useRef(new Set<string>())

  const cardInteractionsHook = useCardInteractions({
    contentId,
    item: link,
    itemIndex,
    hoverStartTimeRef: hoverStartTimeRef ?? defaultHoverStartTimeRef,
    impressionTimeoutRef: impressionTimeoutRef ?? defaultImpressionTimeoutRef,
    hasTrackedImpression: hasTrackedImpression ?? defaultHasTrackedImpression,
    createInteraction: createInteraction ?? (() => null),
    onClickTrack,
  })

  const handleMouseEnter = shouldUseInteractions ? cardInteractionsHook.handleMouseEnter : undefined
  const handleMouseLeave = shouldUseInteractions ? cardInteractionsHook.handleMouseLeave : undefined
  const handleClick = shouldUseInteractions ? cardInteractionsHook.handleClick : onClickTrack
  const handleInViewChange = shouldUseInteractions ? cardInteractionsHook.handleInViewChange : undefined

  if (!link.imageUrl) return null

  return (
    <InView onChange={handleInViewChange} threshold={IMPRESSION_VISIBILITY_THRESHOLD}>
      <div
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={cardRef}
        className="relative"
      >
        <InternalLink
          href={link.linkUrl}
          aria-label={`project-tile-${link.name}`}
          className={classNames(
            'min-w-0 relative block overflow-hidden rounded-lg transition-transform duration-300 ease-in-out transform-gpu [backface-visibility:hidden] group-hover/rail:will-change-transform hover:z-10 md:hover:scale-[1.03]',
            { 'inline-block h-fit': props.isGrid },
            className,
          )}
        >
          <When condition={props.isGrid}>
            <Image
              aria-label={link.alt}
              alt={link.alt}
              src={getCloudinaryImageUrl({
                path: link.imageUrl,
                transforms: 'w_294',
              })}
              height={490}
              width={294}
              className="rounded-lg bg-black"
            />
            <GuildAccessNotch
              isInEarlyAccess={link.isInEarlyAccess}
              isGuildMember={isGuildMember}
              loading={props.loading}
            />
          </When>
          <When condition={!props.isGrid}>
            <div className={classNames('relative w-full rounded-lg bg-black', isLoadingImage ? 'animate-pulse' : '')}>
              <div className="relative w-full" style={{ paddingTop: '160%' }}>
                <Image
                  aria-label={link.alt}
                  alt={link.alt}
                  src={getCloudinaryImageUrl({
                    path: link.imageUrl,
                    transforms: 'w_294',
                  })}
                  fill
                  sizes="(min-width: 1024px) 294px, 33vw"
                  className="absolute inset-0 rounded-lg object-cover"
                  onLoad={() => setIsLoadingImage(false)}
                />
                <GuildAccessNotch
                  isInEarlyAccess={link.isInEarlyAccess}
                  isGuildMember={isGuildMember}
                  loading={props.loading}
                />
              </div>
            </div>
          </When>
        </InternalLink>
      </div>
    </InView>
  )
}
